"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeEnergyWeekComparison = exports.getRollupHourlyKwh = void 0;
const MIN_COMPARABLE_HOURS = 3;
const getRollupHourlyKwh = (hourData) => {
    // Access the averageMainCt1/2 and averageVt1/2 values from either direct properties
    // or from the powerUsageRollup object if it exists
    const mainCt1 = hourData.averageMainCt1 || hourData.powerUsageRollup?.averageMainCt1 || 0;
    const mainCt2 = hourData.averageMainCt2 || hourData.powerUsageRollup?.averageMainCt2 || 0;
    const vt1 = hourData.averageVt1 || hourData.powerUsageRollup?.averageVt1 || 0;
    const vt2 = hourData.averageVt2 || hourData.powerUsageRollup?.averageVt2 || 0;
    // Hourly averages, so watts / 1000 is kWh directly (kW * 1 hour)
    return (mainCt1 * vt1 + mainCt2 * vt2) / 1000;
};
exports.getRollupHourlyKwh = getRollupHourlyKwh;
const getRollupHour = (row) => {
    if (typeof row.hour === 'number')
        return row.hour;
    const hourPart = row.dateHour?.split('-')[3];
    const parsed = hourPart !== undefined ? Number(hourPart) : NaN;
    return Number.isNaN(parsed) ? null : parsed;
};
const toLocalDateString = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/**
 * Week-over-week comparison over LIKE-FOR-LIKE hours only.
 *
 * Today's rollups only cover the hours that have elapsed, while last week's day
 * has all 24 — comparing the two raw totals always reads "less than last week"
 * until late evening. Instead we sum both days over the intersection of hours
 * present on BOTH days: partial-day today vs the same hours last week, and any
 * hour a day is missing entirely (e.g. the midnight hour-24 aggregation bug)
 * drops out of both sides.
 */
const computeEnergyWeekComparison = (powerData, energyRatePerKwh, now = new Date()) => {
    const todayStr = toLocalDateString(now);
    const lastWeekDate = new Date(now);
    lastWeekDate.setDate(now.getDate() - 7);
    const lastWeekStr = toLocalDateString(lastWeekDate);
    const costByHour = (dateStr) => {
        const map = new Map();
        powerData.forEach((row) => {
            if (row.date !== dateStr)
                return;
            const hour = getRollupHour(row);
            if (hour === null)
                return;
            map.set(hour, (map.get(hour) ?? 0) + (0, exports.getRollupHourlyKwh)(row) * energyRatePerKwh);
        });
        return map;
    };
    const todayByHour = costByHour(todayStr);
    const lastWeekByHour = costByHour(lastWeekStr);
    const comparableHours = Array.from(todayByHour.keys()).filter((h) => lastWeekByHour.has(h));
    if (comparableHours.length < MIN_COMPARABLE_HOURS)
        return null;
    const todaysCostToDate = comparableHours.reduce((sum, h) => sum + (todayByHour.get(h) ?? 0), 0);
    const lastWeekCostToDate = comparableHours.reduce((sum, h) => sum + (lastWeekByHour.get(h) ?? 0), 0);
    if (lastWeekCostToDate <= 0)
        return null;
    const change = ((todaysCostToDate - lastWeekCostToDate) / lastWeekCostToDate) * 100;
    return {
        percentageChange: Math.abs(Math.round(change)),
        isIncrease: change > 0,
        todaysCostToDate: Number(todaysCostToDate.toFixed(2)),
        lastWeekCostToDate: Number(lastWeekCostToDate.toFixed(2)),
        comparableHours: comparableHours.length,
    };
};
exports.computeEnergyWeekComparison = computeEnergyWeekComparison;
